"use client";

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  getSettings,
  updateGeneral,
  updateSocial,
  addLocation,
  updateLocation,
  deleteLocation,
  updateDelivery,
  resetAiUsage,
  updateSlickpay,
} from "../api/settings.api";
import { toast } from "sonner";
import type {
  UpdateSocialPayload,
  AddLocationPayload,
  UpdateLocationPayload,
  UpdateDeliveryPayload,
  SiteSettings,
  SocialMedia,
  StoreLocation,
  DeliverySettings,
} from "@/types/settings.types";

export const settingsQueryKeys = {
  all: ["settings"] as const,
};

/**
 * Hook to fetch all site settings
 */
export const useSettings = (
  options?: Partial<UseQueryOptions<SiteSettings, AxiosError>>
) => {
  return useQuery({
    queryKey: settingsQueryKeys.all,
    queryFn: getSettings,
    ...options,
  });
};

/**
 * Hook to update general settings
 */
export const useUpdateGeneral = (
  options?: Partial<UseMutationOptions<SiteSettings, AxiosError, FormData, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: FormData) => updateGeneral(payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("General settings updated successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as any)?.message ||
          "Failed to update general settings"
      );
      console.error("Failed to update general settings:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * Hook to update social media links
 */
export const useUpdateSocial = (
  options?: Partial<UseMutationOptions<SocialMedia, AxiosError, UpdateSocialPayload, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateSocialPayload) => updateSocial(payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("Social media settings updated successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as any)?.message ||
          "Failed to update social settings"
      );
      console.error("Failed to update social settings:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * Hook to add a new location
 */
export const useAddLocation = (
  options?: Partial<UseMutationOptions<StoreLocation, AxiosError, AddLocationPayload, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AddLocationPayload) => addLocation(payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("Location added successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as any)?.message || "Failed to add location"
      );
      console.error("Failed to add location:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * Hook to update an existing location
 */
export const useUpdateLocation = (
  options?: Partial<
    UseMutationOptions<
      StoreLocation,
      AxiosError,
      { id: string; payload: UpdateLocationPayload },
      unknown
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateLocationPayload;
    }) => updateLocation(id, payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("Location updated successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as any)?.message || "Failed to update location"
      );
      console.error("Failed to update location:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * Hook to delete a location
 */
export const useDeleteLocation = (
  options?: Partial<UseMutationOptions<void, AxiosError, string, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteLocation(id),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("Location deleted successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as any)?.message || "Failed to delete location"
      );
      console.error("Failed to delete location:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * Hook to update delivery settings
 */
export const useUpdateDelivery = (
  options?: Partial<UseMutationOptions<DeliverySettings, AxiosError, UpdateDeliveryPayload, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateDeliveryPayload) => updateDelivery(payload),
    onSuccess: (data, variables, context) => {
      // Merge returned delivery settings into cache immediately so Actif/Inactif
      // and credentials persist in the UI without waiting on a refetch race.
      queryClient.setQueryData<SiteSettings>(settingsQueryKeys.all, (prev) =>
        prev ? { ...prev, delivery: { ...prev.delivery, ...data } as SiteSettings["delivery"] } : prev
      );
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("Delivery settings updated successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as any)?.message ||
          "Failed to update delivery settings"
      );
      console.error("Failed to update delivery settings:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * Hook to reset AI usage
 */
export const useResetAiUsage = (
  options?: Partial<UseMutationOptions<void, AxiosError, void, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resetAiUsage,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("AI usage reset successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
         (error.response?.data as any)?.message || "Failed to reset AI usage"
      );
      console.error("Failed to reset AI usage:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * Hook to update SlickPay settings
 */
export const useUpdateSlickpay = (
  options?: Partial<UseMutationOptions<any, AxiosError, {
    slickpayEnabled: boolean;
    publicKey?: string;
    accountUuid?: string;
    isProduction: boolean;
    fees: number;
  }, unknown>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => updateSlickpay(payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.all });
      toast.success("SlickPay settings updated successfully");
      (options?.onSuccess as any)?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as any)?.message ||
          "Failed to update SlickPay settings"
      );
      console.error("Failed to update SlickPay settings:", error, variables, context);
      (options?.onError as any)?.(error, variables, context);
    },
    ...options,
  });
};
